import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Search, ShoppingCart, DollarSign, Plus, Download } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { adminService } from '../../services/adminService';
import CreateClientDialog from './clients/CreateClientDialog';
import { ExportDialog } from '../../components/exports/ExportDialog';
import { toast } from 'sonner';
import { PAYMENT_TERMS_OPTIONS } from './clients/clientsConstants';
import { ClientCard } from './clients/ClientCard';
import { DeleteClientDialog } from './clients/DeleteClientDialog';
import { ClientDetailDialog } from './clients/ClientDetailDialog';
import { useClientDetail } from './clients/useClientDetail';

const EMPTY_CREATE_FORM = {
  company_name: '',
  contact_name: '',
  email: '',
  phone: '',
  address: '',
  password: '',
  minimum_order_amount: 50,
  payment_terms: 'net_30',
};

const AdminClients = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [creating, setCreating] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const loadClients = async () => {
    try {
      const data = await adminService.getClients();
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const detail = useClientDetail({ onClientsChanged: loadClients });

  // Deep link from an order's location editor: /admin/clients?client=<id>&tab=locations
  const deepLinkClient = searchParams.get('client');
  useEffect(() => {
    if (!deepLinkClient || clients.length === 0) return;
    const match = clients.find((c) => c.id === deepLinkClient);
    if (match) detail.openClient(match);
    const next = new URLSearchParams(searchParams);
    next.delete('client');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkClient, clients]);

  const handleCreateClient = async () => {
    if (!createForm.company_name || !createForm.contact_name || !createForm.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/create`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createForm),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create client');
      }

      toast.success(`Client ${createForm.company_name} created successfully`);
      setShowCreateDialog(false);
      setCreateForm(EMPTY_CREATE_FORM);
      loadClients();
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error(error.message || 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;

    setDeleting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${clientToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete client');
      }

      toast.success(`Client ${clientToDelete.company_name} deleted successfully`);
      setShowDeleteDialog(false);
      setClientToDelete(null);
      detail.setDetailOpen(false);
      loadClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error(error.message || 'Failed to delete client');
    } finally {
      setDeleting(false);
    }
  };

  const handleRequestDelete = (client) => {
    setClientToDelete(client);
    setShowDeleteDialog(true);
  };

  const filteredClients = clients.filter(client =>
    client.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.account_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalOpenBalance = clients.reduce((sum, c) => sum + c.open_balance, 0);
  const totalOrders = clients.reduce((sum, c) => sum + c.order_count, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-clients">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wholesale Clients</h1>
          <p className="text-gray-500">Manage client accounts and preferences</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setExportOpen(true)} data-testid="export-clients-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-amber-500 hover:bg-amber-600">
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: Users, bg: 'bg-blue-100', color: 'text-blue-600', label: 'Total Clients', value: clients.length },
          { icon: ShoppingCart, bg: 'bg-green-100', color: 'text-green-600', label: 'Total Orders', value: totalOrders },
          { icon: DollarSign, bg: 'bg-amber-100', color: 'text-amber-600', label: 'Open Balance', value: `$${totalOpenBalance.toFixed(2)}` },
        ].map(({ icon: Icon, bg, color, label, value }) => (
          <Card key={label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 ${bg} rounded-lg`}>
                  <Icon className={`h-5 w-5 ${color}`} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{label}</p>
                  <p className="text-2xl font-bold">{value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Clients Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClients.map((client) => (
          <ClientCard
            key={client.id}
            client={client}
            onView={detail.openClient}
            onDelete={handleRequestDelete}
          />
        ))}
      </div>

      {filteredClients.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No clients found</p>
        </div>
      )}

      <ClientDetailDialog detail={detail} initialTab={searchParams.get('tab') || 'overview'} />

      <CreateClientDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        createForm={createForm}
        setCreateForm={setCreateForm}
        paymentTermsOptions={PAYMENT_TERMS_OPTIONS}
        creating={creating}
        onCreateClient={handleCreateClient}
      />

      <DeleteClientDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        client={clientToDelete}
        deleting={deleting}
        onConfirm={handleDeleteClient}
      />

      <ExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        entity="clients"
        title="Export Clients"
        description="Choose columns to include. Active page filters are honored."
        supportsDateRange={false}
        extraFilters={{
          ...(searchQuery ? { search: searchQuery } : {}),
        }}
        extraFilterSummary={[
          ...(searchQuery ? [{ label: 'Search', value: searchQuery }] : []),
        ]}
      />
    </div>
  );
};

export default AdminClients;
